import { getObjAttribute } from "utils/utils";
import React, { useState } from "react";
import { InputText } from "primereact/inputtext";
import { Checkbox } from "primereact/checkbox";
import { Dropdown } from "primereact/dropdown";
import ColorPicker from "components/basic/color-picker/ColorPicker";

export namespace Input {
  interface BaseProps<KT> {
    id: string;
    label: string;
    property: KT;
    sourceObj: any | undefined;
    editable?: boolean;
    onChange: (prop: KT, value: any) => void;
  }

  interface TextProps<KT> extends BaseProps<KT> {}

  interface BooleanProps<KT> extends BaseProps<KT> {}

  interface ListProps<KT> extends BaseProps<KT> {
    options: Array<any>;
    editable?: boolean;
    filter?: boolean;
    grouped?: boolean;
  }

  interface NumberProps<KT> extends BaseProps<KT> {}

  export function Text<KT>(props: TextProps<KT>) {
    const value = getObjAttribute(props.sourceObj, props.property, "");
    return (
      <>
        <InputText
          id={props.id}
          type="text"
          className="p-inputtext-sm w-full"
          placeholder={props.label}
          value={value}
          onChange={e => props.onChange(props.property, e.target.value)}
          disabled={false || props.editable}
        />
        <label htmlFor={props.id}>{props.label}</label>
      </>
    );
  }

  export function Boolean<KT>(props: BooleanProps<KT>) {
    const value = getObjAttribute(props.sourceObj, props.property, false);
    return (
      <div className="flex align-items-center">
        <Checkbox
          id={props.id}
          className=""
          checked={value}
          onChange={e => props.onChange(props.property, e.checked)}
        />
        <label className="ml-2" htmlFor={props.id}>
          {props.label}
        </label>
      </div>
    );
  }

  export function List<KT>(props: ListProps<KT>) {
    const value = getObjAttribute(props.sourceObj, props.property);
    return (
      <div className="flex align-items-center">
        <Dropdown
          id={props.id}
          className="p-inputtext-sm w-full"
          value={value}
          options={props.options}
          filter={props.filter}
          filterMatchMode="contains"
          optionGroupChildren={props.grouped ? "items" : undefined}
          optionGroupLabel={props.grouped ? "label" : undefined}
          onChange={(e: any) => props.onChange(props.property, e.value)}
          editable={props.editable || false}
        />
        <label className="ml-2" htmlFor={props.id}>
          {props.label}
        </label>
      </div>
    );
  }

  export function Number<KT>(props: NumberProps<KT>) {
    const [valid, setValid] = useState(true);
    const value = getObjAttribute(props.sourceObj, props.property, "");

    const onChange = (e: any) => {
      if (e.target.value === "") {
        setValid(true);
        props.onChange(props.property, undefined);
        return;
      }

      const newValue = parseInt(e.target.value);
      if (isNaN(newValue)) {
        setValid(false);
      } else {
        setValid(true);
        props.onChange(props.property, newValue);
      }
    };

    return (
      <>
        <InputText
          id={props.id}
          type="number"
          className={`p-inputtext-sm w-full ${valid ? "" : "p-invalid"}`}
          placeholder={props.label}
          value={value}
          onChange={onChange}
        />
        <label htmlFor={props.id}>{props.label}</label>
      </>
    );
  }

  export function Color<KT>(props: BaseProps<KT>) {
    const value = getObjAttribute(props.sourceObj, props.property, "");

    return (
      <div className="flex align-items-center justify-content-end gap-2">
        <label htmlFor={props.id}>{props.label}</label>
        <div id={props.id} className="flex align-items-center gap-2">
          <ColorPicker
            id={props.id}
            value={value || "#ffffff"}
            onValueChange={event => props.onChange(props.property, event.target.value)}
          />
          <i
            className="pi pi-refresh cursor-pointer"
            onClick={() => props.onChange(props.property, undefined)}
          />
        </div>
      </div>
    );
  }
}
